import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiClientError, apiGet } from '../lib/api';

export type ApiState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; data: T }
  | { status: 'error'; error: ApiClientError | Error };

// Thin wrapper over TanStack Query's useQuery that preserves the original
// useApi() return shape so the existing ~50 callers across the app keep
// working without a signature change. Migration to direct `useQuery` is
// happening incrementally — see TodayScreen.tsx for the first conversion.
//
// Behaviour parity vs. the previous bare-useEffect implementation:
//   - `path === null`        → status 'idle' (query stays disabled).
//   - in-flight              → status 'loading'.
//   - success                → status 'ok' with typed data.
//   - error (any throw)      → status 'error' with the original Error.
//   - `refresh()`            → invalidates the query so any mounted
//                              consumer of the same key refetches.
//
// Why a single-element string array for the key:
//   TanStack Query keys are structural; using `[path]` deduplicates
//   identical paths across screens for free (a Today and Insights
//   screen both calling `/api/taper` share one in-flight request +
//   one cache slot). The previous useApi gave every caller its own
//   useState + useEffect, so the same path fired N times in parallel.
//
// Caveats kept in mind for any future direct `useQuery` migration:
//   - `apiGet` throws ApiClientError on non-2xx; useQuery surfaces that
//     as `error` unchanged, so callers that pattern-match on the class
//     keep working.
//   - We don't pass `enabled` for empty-string paths because the audit
//     contract was `null = idle`. Empty-string paths were never valid.
export function useApi<T>(path: string | null): ApiState<T> & { refresh: () => void } {
  const queryClient = useQueryClient();
  const enabled = path != null;

  const query = useQuery<T, ApiClientError | Error>({
    // queryKey MUST be stable for the cache to dedupe / share. We use
    // the path string itself; callers that interpolate query-string
    // params (e.g. `?from=...&tz=...`) get distinct keys per param
    // set, which is what we want.
    queryKey: ['useApi', path ?? '__idle__'],
    queryFn: () => apiGet<T>(path as string),
    enabled,
  });

  const refresh = useCallback(() => {
    if (!enabled) return;
    void queryClient.invalidateQueries({ queryKey: ['useApi', path] });
  }, [queryClient, path, enabled]);

  if (!enabled) {
    return { status: 'idle', refresh };
  }
  // useQuery starts in 'pending' state when enabled but not yet resolved.
  if (query.status === 'pending') {
    return { status: 'loading', refresh };
  }
  if (query.status === 'error') {
    return { status: 'error', error: query.error, refresh };
  }
  return { status: 'ok', data: query.data, refresh };
}
